import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { CheckIcon, LoaderIcon, CircleIcon } from 'lucide-react';
import { DEMO_DEAL } from '../../data/dgpt';
import { SectionHeading } from '../ui/SectionHeading';
import { Reveal } from '../ui/Reveal';
import { cn } from '../../src/lib/utils';
const PIPELINE = [{
  label: 'Deal enters pipeline',
  detail: 'Founder portal · 5 files'
}, {
  label: 'Fit Score generated',
  detail: `${DEMO_DEAL.fitScore}/100 · Healthcare Fund`
}, {
  label: 'Market intelligence',
  detail: 'Market map · competitors'
}, {
  label: 'Risk analysis',
  detail: '3 flags for review'
}, {
  label: 'Comparables',
  detail: 'Benchmarked against prior deals'
}, {
  label: 'IC memo',
  detail: 'Draft ready for partner review'
}];
const LINES = ['Every startup needs to be screened.', 'Every thesis needs to be applied consistently.', 'Every market needs to be researched.', 'Every risk needs to be understood.', 'Every opportunity needs to be compared against everything else the fund has seen.'];
export function SpeedSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: '-25% 0px'
  });
  const reduceMotion = useReducedMotion();
  const [step, setStep] = useState(reduceMotion ? PIPELINE.length : 0);
  useEffect(() => {
    if (reduceMotion) {
      setStep(PIPELINE.length);
      return;
    }
    if (!inView) return;
    const delay = step >= PIPELINE.length ? 3200 : 1100;
    const id = window.setTimeout(() => setStep(value => value >= PIPELINE.length ? 0 : value + 1), delay);
    return () => window.clearTimeout(id);
  }, [inView, step, reduceMotion]);
  return <section aria-labelledby="speed-heading" className="border-b border-hairline-soft bg-ink" data-id="element-367" data-mp="35xlh40aor729">
      <div className="mx-auto grid w-full max-w-[1320px] gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-28" data-id="element-368" data-mp="2ryluey6qh28q">
        <div className="flex flex-col gap-6" data-id="element-369" data-mp="2ww3sftj7px1y">
          <SectionHeading eyebrow="Why it matters" headingId="speed-heading" title={<>
                Diligence at the <em data-id="element-371" data-mp="xox4ccqyja3v">Speed of Dealflow.</em>
              </>} data-id="element-370" data-mp="12h6plfh4nlzt" />
          <Reveal delay={0.06} className="flex flex-col gap-5" data-id="element-372" data-mp="tpy0ywkvjlej">
            <p className="text-lg font-medium leading-snug text-text-primary" data-id="element-373" data-mp="oq1xqzk03y01">
              The best investment teams aren&apos;t short on opportunities.
              <br data-id="element-374" data-mp="1lfj1va3c5bzm" />
              They&apos;re short on time.
            </p>
            <ul className="flex flex-col gap-2 border-l border-hairline pl-4" data-id="element-375" data-mp="1hfiolsrz36u5">
              {LINES.map(line => <li key={line} className="text-sm text-text-secondary" data-id="element-376" data-mp="1cdtag49ged1u">
                  {line}
                </li>)}
            </ul>
            <p className="text-[15px] leading-relaxed text-text-secondary" data-id="element-377" data-mp="zxwuzy76ccq">
              DiligenceGPT helps investment teams structure that work so their analysts and partners
              can spend more time making decisions — and less time assembling information.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-378" data-mp="3n0vi18qupc1d">
          <div ref={ref} className="rounded-2xl border border-hairline bg-panel" data-id="element-379" data-mp="3n26iozp023o9">
            <div className="flex items-center justify-between border-b border-hairline px-4 py-3" data-id="element-380" data-mp="3rzq6orbi1ute">
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-secondary" data-id="element-381" data-mp="n5a0bwxakci">
                {DEMO_DEAL.name} · Workflow
              </span>
              <span className="font-mono text-[11px] text-text-tertiary" data-id="element-382" data-mp="9m8wlpvathvr">
                {Math.min(step, PIPELINE.length)}/{PIPELINE.length}
              </span>
            </div>
            <ol className="flex flex-col gap-2 p-4" data-id="element-383" data-mp="jky3knlnphx8">
              {PIPELINE.map((stage, index) => {
              const done = index < step;
              const running = index === step;
              return <li key={stage.label} className={cn('flex items-center gap-3 rounded-xl border px-3 py-3 transition-colors duration-300', done && 'border-hairline-soft bg-elevated', running && 'border-lime/35 bg-lime/[0.06]', !done && !running && 'border-hairline-soft bg-transparent opacity-50')} data-id="element-384" data-mp="2lbt4ftuq07at" data-mpi={index}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-hairline bg-panel" data-id="element-385" data-mp="jnt60ym5hlt6">
                      {done ? <CheckIcon className="h-3.5 w-3.5 text-lime" aria-hidden="true" data-id="element-386" data-mp="3o5f5sim0pvz1" /> : running ? <LoaderIcon className="h-3.5 w-3.5 animate-spin text-lime motion-reduce:animate-none" aria-hidden="true" data-id="element-387" data-mp="5rpfbfahjvlg" /> : <CircleIcon className="h-3 w-3 text-text-tertiary" aria-hidden="true" data-id="element-388" data-mp="39dmymtwdno8e" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-389" data-mp="1jxu659sydgy6">
                      <span className="block text-sm text-text-primary" data-id="element-390" data-mp="1jx0xu4ecijdx">{stage.label}</span>
                      <span className="block truncate text-[11px] text-text-tertiary" data-id="element-391" data-mp="1opkhnj3mmar6">{stage.detail}</span>
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary" data-id="element-392" data-mp="1av1eh5fuz37t">
                      {done ? 'Done' : running ? 'Running' : 'Queued'}
                    </span>
                  </li>;
            })}
            </ol>
            <p className="border-t border-hairline px-4 py-3 text-[11px] text-text-tertiary" data-id="element-393" data-mp="27m3pm52p1sm5">
              Analysts and partners review every output.
            </p>
          </div>
        </Reveal>
      </div>
    </section>;
}